import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for managing state that persists across browser sessions using localStorage
 * @param key - The localStorage key to use
 * @param defaultValue - The default value if nothing is stored
 * @returns [state, setState] tuple similar to useState
 */
export function useLocalStorage<T>(key: string, defaultValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  // Initialize state from localStorage or use default
  const [state, setState] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  // Update localStorage when state changes
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, state]);

  return [state, setState];
}

/**
 * Hook for managing state that persists during the browser session using sessionStorage
 * Useful for search queries, pagination, etc. that should persist when navigating back
 * @param key - The sessionStorage key to use
 * @param defaultValue - The default value if nothing is stored
 * @returns [state, setState] tuple similar to useState
 */
export function useSessionStorage<T>(key: string, defaultValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  // Initialize state from sessionStorage or use default
  const [state, setState] = useState<T>(() => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading sessionStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  // Update sessionStorage when state changes
  useEffect(() => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.warn(`Error setting sessionStorage key "${key}":`, error);
    }
  }, [key, state]);

  return [state, setState];
}

/**
 * Hook for managing browser history state that persists when using back/forward buttons
 * This provides a more seamless navigation experience
 * @param key - The history state key to use
 * @param defaultValue - The default value if nothing is stored
 * @returns [state, setState] tuple similar to useState
 */
export function useHistoryState<T>(key: string, defaultValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  // Track if we're mounting to avoid unnecessary history pushes
  const isMounting = useRef(true);

  // Initialize state from history.state or use default
  const [state, setState] = useState<T>(() => {
    try {
      const historyState = window.history.state;
      if (historyState && key in historyState) {
        return historyState[key];
      }
      // Also check sessionStorage as fallback
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading history state key "${key}":`, error);
      return defaultValue;
    }
  });

  // Update history state when state changes
  useEffect(() => {
    if (isMounting.current) {
      isMounting.current = false;
      return;
    }

    try {
      // Get current history state and merge in our key
      const currentState = window.history.state || {};
      const newState = { ...currentState, [key]: state };

      // Replace the current history state (don't create new history entry)
      window.history.replaceState(newState, '');

      // Also update sessionStorage as backup
      window.sessionStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.warn(`Error setting history state key "${key}":`, error);
    }
  }, [key, state]);

  // Listen for popstate events (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      try {
        const historyState = window.history.state;
        if (historyState && key in historyState) {
          setState(historyState[key]);
        }
      } catch (error) {
        console.warn(`Error reading history state on popstate for key "${key}":`, error);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [key]);

  return [state, setState];
}

/**
 * Combined hook that uses localStorage for persistent settings and sessionStorage for navigation state
 * @param persistentKey - The localStorage key for persistent settings
 * @param sessionKey - The sessionStorage key for session state
 * @param defaultValue - The default value
 * @returns Object with persistent and session state/setters
 */
export function useCombinedStorage<T>(
  persistentKey: string,
  sessionKey: string,
  defaultValue: T
): {
  persistentState: T;
  setPersistentState: (value: T | ((prev: T) => T)) => void;
  sessionState: T;
  setSessionState: (value: T | ((prev: T) => T)) => void;
} {
  const [persistentState, setPersistentState] = useLocalStorage(persistentKey, defaultValue);
  const [sessionState, setSessionState] = useHistoryState(sessionKey, defaultValue);

  return {
    persistentState,
    setPersistentState,
    sessionState,
    setSessionState,
  };
}
